/* BodyMeasurementsCard — бърз inline ъпдейт на тегло/талия/ръст
 * (iter421). Тези данни са необходими за PDIL (Personalised DIL),
 * VAIS и метаболитен фенотип. Без тях много модули показват "—".
 *
 * Стратегия: винаги видимо на HI page → 4 inputs (height/weight/waist/hip)
 * + 1 бутон "Запази" → POST /api/health-intelligence/body-measurements.
 * След ъпдейт извикваме parent.onSaved() за refresh на summary KPI cards.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ruler, Scale, Save, Loader2, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const FIELDS = [
  { id: "weight_kg", icon: Scale,  unit: "kg",  min: 30,  max: 250, bg: "Тегло",  en: "Weight",  step: 0.1 },
  { id: "waist_cm",  icon: Ruler,  unit: "cm",  min: 40,  max: 200, bg: "Талия",  en: "Waist",   step: 0.5 },
  { id: "height_cm", icon: Ruler,  unit: "cm",  min: 100, max: 230, bg: "Ръст",   en: "Height",  step: 0.5 },
  { id: "hip_cm",    icon: Ruler,  unit: "cm",  min: 40,  max: 200, bg: "Ханш",   en: "Hip",     step: 0.5 },
];

export default function BodyMeasurementsCard({ summary, onSaved }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [vals, setVals] = useState({ weight_kg: "", waist_cm: "", height_cm: "", hip_cm: "" });
  const [saving, setSaving] = useState(false);

  // Pre-fill от текущия summary
  useEffect(() => {
    if (!summary?.body) return;
    setVals((v) => ({
      ...v,
      weight_kg: summary.body.weight_kg ?? "",
      waist_cm:  summary.body.waist_cm  ?? "",
      height_cm: summary.body.height_cm ?? "",
      hip_cm:    summary.body.hip_cm    ?? "",
    }));
  }, [summary]);

  const dirty = FIELDS.some((f) => {
    const cur = vals[f.id];
    const orig = summary?.body?.[f.id];
    return cur !== "" && Number(cur) !== Number(orig);
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        const v = vals[f.id];
        if (v !== "" && !isNaN(Number(v))) payload[f.id] = Number(v);
      });
      if (Object.keys(payload).length === 0) {
        toast.error(bg ? "Попълни поне едно поле" : "Fill at least one field");
        setSaving(false);
        return;
      }
      await axios.post(`${API}/health-intelligence/body-measurements`, payload);
      toast.success(bg ? "Записано ✓" : "Saved ✓");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
          data-testid="body-measurements-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Scale className={`w-4 h-4 ${isDark ? "text-sky-300" : "text-sky-600"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Телесни измервания" : "Body measurements"}
          </h3>
          <span className={`text-[10px] ml-auto ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg ? "необходими за DIL и фенотип" : "required for DIL & phenotype"}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {FIELDS.map(({ id, icon: Icon, unit, min, max, bg: bgL, en, step }) => (
            <div key={id}>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 flex items-center gap-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Icon className="w-3 h-3" />
                {bg ? bgL : en}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={vals[id]}
                  onChange={(e) => setVals((v) => ({ ...v, [id]: e.target.value }))}
                  placeholder="—"
                  className={`pr-10 ${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                  data-testid={`bm-${id}`}
                />
                <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold pointer-events-none ${
                      isDark ? "text-slate-500" : "text-slate-400"
                    }`}>
                  {unit}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <p className={`text-[10.5px] flex items-center gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            <Info className="w-3 h-3" />
            {bg
              ? "Талия се мерва на нивото на пъпа, на празен стомах."
              : "Measure waist at navel level, on an empty stomach."}
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
            data-testid="bm-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {bg ? "Запази" : "Save"}
          </Button>
        </div>

        {/* iter425 — Derived indices: BMI, WHR, WHtR, VAIS */}
        {(summary?.body?.bmi || summary?.body?.whr || summary?.body?.whtr || summary?.body?.vais_score) && (
          <div className={`pt-3 mt-2 border-t ${isDark ? "border-slate-800" : "border-slate-200"}`} data-testid="bm-derived">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg ? "Изчислени индекси" : "Derived indices"}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {summary?.body?.bmi != null && (
                <div title={bg ? "BMI = тегло (kg) / ръст² (m). Норма: 18.5-25." : "BMI = weight / height²"}
                     className={`p-2 rounded-lg text-center cursor-help ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
                  <p className={`text-[9.5px] uppercase ${isDark ? "text-slate-400" : "text-slate-500"}`}>BMI</p>
                  <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{summary.body.bmi}</p>
                </div>
              )}
              {summary?.body?.whr != null && (
                <div title={bg ? "WHR = талия/ханш. М <0.90, Ж <0.85 = ниска висцерална мазнина." : "WHR = waist/hip ratio"}
                     className={`p-2 rounded-lg text-center cursor-help ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
                  <p className={`text-[9.5px] uppercase ${isDark ? "text-slate-400" : "text-slate-500"}`}>WHR</p>
                  <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{summary.body.whr}</p>
                </div>
              )}
              {summary?.body?.whtr != null && (
                <div title={bg ? "WHtR = талия/ръст. <0.50 = метаболитно здраво." : "WHtR = waist/height ratio"}
                     className={`p-2 rounded-lg text-center cursor-help ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
                  <p className={`text-[9.5px] uppercase ${isDark ? "text-slate-400" : "text-slate-500"}`}>WHtR</p>
                  <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{summary.body.whtr}</p>
                </div>
              )}
              {summary?.body?.vais_score != null && (
                <div title={bg ? "VAIS — Visceral Adiposity Score (0-100). Композиция от талия, BMI, метаболизъм, начин на живот." : "VAIS — Visceral Adiposity Score (0-100)"}
                     className={`p-2 rounded-lg text-center cursor-help ${
                       (summary.body.vais_score || 0) < 40 ? (isDark ? "bg-emerald-500/15" : "bg-emerald-50") :
                       (summary.body.vais_score || 0) < 60 ? (isDark ? "bg-amber-500/15" : "bg-amber-50") :
                       (isDark ? "bg-rose-500/15" : "bg-rose-50")
                     }`} data-testid="bm-vais-chip">
                  <p className={`text-[9.5px] uppercase ${isDark ? "text-slate-400" : "text-slate-500"}`}>VAIS</p>
                  <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {Math.round(summary.body.vais_score)}
                    <span className={`text-[9px] ml-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>/100</span>
                  </p>
                  {summary.body.vais_band && (
                    <p className={`text-[9px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{summary.body.vais_band}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
